import { ModalShell } from '../../admin/modals/ModalShell'
import { useSupplierDetail } from './useDrilldown'

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
  })
}

export function SupplierDetailModal({
  supplierName,
  onClose,
}: {
  supplierName: string
  onClose: () => void
}) {
  const { data, isLoading, isError } = useSupplierDetail(supplierName)

  return (
    <ModalShell
      title={supplierName}
      subtitle={data ? `Поставщик · ${data.stats.quotesCount} квот от меня` : undefined}
      onClose={onClose}
      size="lg"
    >
      {isLoading && <p className="text-sm text-gray-400">Загрузка…</p>}
      {isError && <p className="text-sm text-red-600">Не удалось загрузить данные</p>}
      {data && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Stat label="Квот" value={data.stats.quotesCount} />
            <Stat
              label="Ср. время"
              value={data.stats.avgResponseHours === null ? '—' : `${data.stats.avgResponseHours}ч`}
            />
            <Stat
              label="Ср. цена"
              value={data.stats.avgPrice === null ? '—' : data.stats.avgPrice.toFixed(2)}
            />
            <Stat label="Уникальных позиций" value={data.stats.uniqueParts} />
          </div>

          {data.topParts.length > 0 && (
            <div>
              <div className="text-xs text-gray-500 uppercase tracking-wide mb-2">
                Топ позиций от поставщика
              </div>
              <div className="divide-y divide-gray-100 rounded-xl border border-gray-100">
                {data.topParts.map((p) => (
                  <div key={p.partNumber} className="flex items-center gap-2 px-3 py-2 text-sm">
                    <span className="font-mono text-xs text-gray-900 flex-1 truncate">
                      {p.partNumber}
                    </span>
                    <span className="text-xs text-gray-500 w-20 truncate">{p.brand ?? '—'}</span>
                    <span className="text-xs text-gray-500 tabular-nums w-14 text-right">
                      {p.quotesCount} квот
                    </span>
                    <span className="text-xs text-gray-700 tabular-nums w-16 text-right">
                      ср. {p.avgPrice.toFixed(2)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div>
            <div className="text-xs text-gray-500 uppercase tracking-wide mb-2">
              Последние квоты
            </div>
            <div className="divide-y divide-gray-100 rounded-xl border border-gray-100 max-h-[220px] overflow-y-auto">
              {data.recentQuotes.map((q) => (
                <div key={q.id} className="flex items-center gap-2 px-3 py-1.5 text-sm">
                  <span className="font-mono text-xs text-gray-900 flex-1 truncate">
                    {q.partNumber}
                  </span>
                  <span className="text-xs text-gray-700 tabular-nums w-24 text-right">
                    {q.price.toFixed(2)} {q.currency}
                  </span>
                  <span className="text-xs text-gray-500 tabular-nums w-16 text-right">
                    {formatDate(q.createdAt)}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </ModalShell>
  )
}

function Stat({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="bg-gray-50 rounded-xl p-3">
      <div className="text-xs text-gray-500">{label}</div>
      <div className="text-xl font-semibold text-gray-900 mt-0.5">{value}</div>
    </div>
  )
}
